/**
 * Plugin to dynamically set HTML dir and lang attributes based on saved locale.
 * This ensures the correct RTL/LTR direction is applied on initial page load.
 *
 * The plugin reads from:
 * 1. localStorage (a4t_admin_locale) - set by LanguageSwitcher
 * 2. Cookie (i18n_locale) - set by @nuxtjs/i18n
 * 3. Falls back to default locale (ar)
 */
export default defineNuxtPlugin(() => {
  // Only run on client side
  if (!import.meta.client) return;

  const rtlLocales = ['ar'];

  // Function to get saved locale
  function getSavedLocale(): string {
    // Check localStorage first (set by our LanguageSwitcher)
    const localStorageLocale = localStorage.getItem('a4t_admin_locale');
    if (localStorageLocale && ['ar', 'en', 'zh'].includes(localStorageLocale)) {
      return localStorageLocale;
    }

    // Check cookie (set by @nuxtjs/i18n)
    const cookies = document.cookie.split(';');
    for (const cookie of cookies) {
      const [name, value] = cookie.trim().split('=');
      if (name === 'i18n_locale' && value && ['ar', 'en', 'zh'].includes(value)) {
        return value;
      }
    }

    return 'ar'; // Default
  }

  // Function to update HTML attributes
  function updateHtmlAttrs(locale: string) {
    const dir = rtlLocales.includes(locale) ? 'rtl' : 'ltr';
    document.documentElement.setAttribute('dir', dir);
    document.documentElement.setAttribute('lang', locale);
  }

  // Set initial direction immediately
  const savedLocale = getSavedLocale();
  updateHtmlAttrs(savedLocale);

  // Watch for locale changes (from LanguageSwitcher)
  // We use a MutationObserver to detect changes to the lang attribute
  // This is a fallback in case the LanguageSwitcher doesn't update the attributes
  // Only `dir` is written here. Re-setting `lang` from a `lang` observer
  // fired the observer again (a mutation is recorded even for the same
  // value), an endless loop that froze the page on every language change.
  const observer = new MutationObserver(() => {
    const newLang = document.documentElement.getAttribute('lang');
    if (!newLang) return;
    const dir = rtlLocales.includes(newLang) ? 'rtl' : 'ltr';
    if (document.documentElement.getAttribute('dir') !== dir) {
      document.documentElement.setAttribute('dir', dir);
    }
  });

  observer.observe(document.documentElement, {
    attributes: true,
    attributeFilter: ['lang'],
  });
});
